import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Paper,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  TableSortLabel,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  TablePagination,
} from '@mui/material';
import {
  Folder,
  InsertDriveFile,
  MoreVert,
  Download,
  Edit,
  Delete,
  CheckCircle,
  Error as ErrorIcon,
  Schedule,
  HourglassEmpty,
  Preview,
  RadioButtonUnchecked,
} from '@mui/icons-material';
import { useCompanyStore } from '@/stores/company-store';
import { useNavigationStore } from '@/stores/navigation-store';
import { useGridData } from '@/hooks/use-grid-data';
import { useQueue } from '@/hooks/use-queue';
import { useFileOperations } from '@/hooks/use-file-operations';
import { DragDropZone, useDragDrop } from '@/components/DragDrop/DragDropZone';
import { useFileMove } from '@/hooks/use-file-move';
import type { GridItem } from '@/services/grid-service';
import { formatBytes as formatFileSize } from '@/utils/format';
import { useFileUpload } from "../../hooks/use-file-upload";
import { UploadProgressPanel } from '../Storage/Upload/UploadProgressPanel';

interface RepoGridProps {
  searchTerm?: string;
  onClearSearch?: () => void;
  onPreviewFile?: (file: GridItem) => void;
  enableDragDrop?: boolean;
}

type SortColumn = 'Name' | 'LastModifiedString' | 'Size';

export function RepoGrid({
  searchTerm = '',
  onClearSearch,
  onPreviewFile,
  enableDragDrop = false,
}: RepoGridProps) {
  const { t, i18n } = useTranslation('load');
  const { activeCompany } = useCompanyStore();
  const { currentPath, navigateToFolder, selectedItems, setSelectedItems } =
    useNavigationStore();

  // Subscribe to queue data (MainPage.tsx handles polling, React Query shares the cache)
  // This causes re-render when queue status changes
  const { data: queueData = [] } = useQueue(activeCompany?.Id, !!activeCompany, activeCompany?.Id === 1);

  const hasActiveOperations = queueData.some(
    (item) => item.Status === 'Processing' || item.Status === 'Pending' || item.Status === 'Blocked'
  );

  const {
    data: rawGridData = [],
    isLoading,
    error,
  } = useGridData(activeCompany?.Id, currentPath, hasActiveOperations);

  // Merge queue status into grid items (matching Vue's setStatus method)
  // Queue items have the real-time processing status, grid items have the stored data
  const gridData = useMemo(() => {
    // Cast to proper type and ensure it's an array
    const rawGridItems: GridItem[] = Array.isArray(rawGridData)
      ? (rawGridData as GridItem[])
      : [];

    if (!queueData.length) return rawGridItems;

    return rawGridItems.map((item) => {
      if (!item.IsFolder) return item;

      // Construct the path to match against queue items
      const itemPath = currentPath ? `${currentPath}/${item.Name}` : item.Name;

      // Find matching queue item by path
      // Path matching handles multiple formats the backend may return:
      // - Exact match (queuePath === itemPath)
      // - With leading slash (queuePath === `/${itemPath}`)
      // - Match by folder name at path end (for nested paths where full path varies)
      const queueItem = queueData.find((q) => {
        const queuePath = q.Path || '';
        // Normalize paths for comparison
        const normalizedQueuePath = queuePath.replace(/^\//, '');
        const normalizedItemPath = itemPath.replace(/^\//, '');
        return (
          normalizedQueuePath === normalizedItemPath ||
          normalizedQueuePath.endsWith(`/${item.Name}`) && normalizedItemPath.endsWith(`/${item.Name}`)
        );
      });

      // Only show queue status for active operations, not Completed
      // Completed items should show IsLoadedApp/"X Sequences Loaded" instead
      if (queueItem && queueItem.Status && queueItem.Status !== 'Completed') {
        // Override status from queue (Processing, Pending, Blocked, Failed)
        return { ...item, Status: queueItem.Status as GridItem['Status'] };
      }

      // No queue match or Completed - clear status so IsLoadedApp can show
      return { ...item, Status: undefined };
    });
  }, [rawGridData, queueData, currentPath]);
  // so we don't need to call useQueue here

  // File operations hook
  const { deleteItems, renameItem, downloadItem } = useFileOperations();
  const { moveItems: handleMoveItems } = useFileMove({
    companyId: activeCompany?.Id?.toString(),
    sourcePath: currentPath,
  });

  // Drag & Drop functionality
  const { startDrag, endDrag } = useDragDrop({
    onItemsDropped: async (itemIds: string[], targetPath: string) => {
      // Move operation: get item names and call handleMoveItems
      const itemNames = itemIds
        .map((id) => gridData.find((item) => item.Id === id)?.Name)
        .filter(Boolean) as string[];
      await handleMoveItems(itemIds, itemNames, targetPath);
    },
  });

  // Use navigation store for selection instead of local state
  // Filter out any items with undefined/empty IDs to prevent checkbox issues
  const selectedIds = useMemo(() => {
    if (!selectedItems || selectedItems.length === 0) return [];
    return selectedItems
      .filter((item) => item && item.Id) // Filter out null/undefined items and items without IDs
      .map((item) => String(item.Id));
  }, [selectedItems]);
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    item: GridItem | null;
  } | null>(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [renamingItem, setRenamingItem] = useState<GridItem | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingItems, setDeletingItems] = useState<string[]>([]);
  const [orderBy, setOrderBy] = useState<SortColumn>('Name');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const gridWithStatus: GridItem[] = useMemo(() => {
    if (!queueData?.length) return gridData;
    const prefix = currentPath ? `${currentPath}/` : '';
    return gridData.map((item) => {
      const expectedPath = `${prefix}${item.Name}`;
      const match = (queueData as Array<{ Path?: string; QueueStatus?: GridItem['Status']; Status?: GridItem['Status'] }>).find(
        (q) => {
          const path = q.Path || '';
          return path.includes(expectedPath);
        },
      );
      const newStatus = (match?.QueueStatus || match?.Status) as GridItem['Status'] | undefined;
      if (!newStatus) return item;
      const normalizedStatus: GridItem['Status'] = newStatus === 'Blocked' ? 'Pending' : newStatus;
      return { ...item, Status: normalizedStatus } as GridItem;
    });
  }, [gridData, queueData, currentPath]);

  // Filter by search (with safety check)
  const filteredData = gridData.filter((item) =>
    item.Name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Sort data
  const sortedData = [...filteredData].sort((a, b) => {
    // Folders first
    if (a.Type === 'Folder' && b.Type === 'File') return -1;
    if (a.Type === 'File' && b.Type === 'Folder') return 1;

    const aValue = (a[orderBy] ?? '') as string | number;
    const bValue = (b[orderBy] ?? '') as string | number;

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return order === 'asc' ? aValue - bValue : bValue - aValue;
    }

    const cmp = String(aValue).localeCompare(String(bValue), i18n.language, { sensitivity: 'base', numeric: true });
    return order === 'asc' ? cmp : -cmp;
  });

  // Paginated data - only show current page
  const paginatedData = sortedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Reset page when search term or path changes
  useEffect(() => {
    setPage(0);
  }, [searchTerm, currentPath]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      // Select all items on current page, filtering out any with undefined IDs
      const validItems = paginatedData.filter((item) => item && item.Id);
      setSelectedItems(validItems);
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectOne = (id: string) => {
    // Don't select items without valid IDs
    if (!id) return;
    
    const item = sortedData.find((item) => item.Id === id);
    if (!item || !item.Id) return;

    const isCurrentlySelected = selectedIds.includes(id);

    if (isCurrentlySelected) {
      // Remove from selection
      const newSelection = selectedItems.filter((selectedItem) => selectedItem.Id !== id);
      setSelectedItems(newSelection);
    } else {
      // Add to selection
      const newSelection = [...selectedItems, item];
      setSelectedItems(newSelection);
    }
  };

  const handleRowClick = (item: GridItem, event: React.MouseEvent) => {
    // Don't navigate if clicking on checkbox or actions
    const target = event.target as HTMLElement;
    if (target.closest('input[type="checkbox"]') || target.closest('button')) {
      return;
    }

    if (item.Type === 'Folder') {
      // Build new path from current path + folder name (same as Vue app)
      const newPath = currentPath ? `${currentPath}/${item.Name}` : item.Name;
      navigateToFolder(newPath);
      // Clear selection and search when navigating into folder
      setSelectedItems([]);
      onClearSearch?.();
    }
  };

  const handleRowDoubleClick = (item: GridItem, event: React.MouseEvent) => {
    // Don't preview if clicking on checkbox or actions
    const target = event.target as HTMLElement;
    if (target.closest('input[type="checkbox"]') || target.closest('button')) {
      return;
    }

    if (item.Type === 'File' && contextMenu && contextMenu.item && onPreviewFile) {
      onPreviewFile(contextMenu.item);
    }
  };

  const handleContextMenu = (event: React.MouseEvent, item: GridItem) => {
    event.preventDefault();
    setContextMenu({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
      item,
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleSort = (property: SortColumn) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusIcon = (status?: string) => {
    switch (status) {
      case 'Processing':
        return <HourglassEmpty sx={{ fontSize: 18 }} />;
      case 'Completed':
        return <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />;
      case 'Failed':
        return <ErrorIcon sx={{ fontSize: 18, color: 'error.main' }} />;
      case 'Pending':
      case 'Blocked':
        return <Schedule sx={{ fontSize: 18, color: 'warning.main' }} />;
      default:
        return null;
    }
  };

  const getStatusLabel = (status: string): string => {
    const statusMap: Record<string, string> = {
      'Processing': t('status.processing'),
      'Completed': t('status.completed'),
      'Failed': t('status.failed'),
      'Pending': t('status.pending'),
      'Blocked': t('status.blocked'),
    };
    return statusMap[status] || status;
  };

  const getStatusChipColor = (status: string): 'success' | 'error' | 'info' | 'warning' => {
    if (status === 'Completed') return 'success';
    if (status === 'Failed') return 'error';
    if (status === 'Processing') return 'info';
    return 'warning';
  };

  const renderStatusCell = (item: GridItem) => {
    // Queue status (Processing, Completed, Failed, Pending, Blocked)
    if (item.Status) {
      return (
        <Chip
          size="small"
          icon={getStatusIcon(item.Status) || undefined}
          label={getStatusLabel(item.Status)}
          variant="outlined"
          color={getStatusChipColor(item.Status)}
        />
      );
    }

    // Loaded application - show sequence count
    if (item.IsLoadedApp) {
      return (
        <Typography variant="body2" color="text.secondary">
          {t('grid.sequencesLoaded', { count: item.LoadedSubs })}
        </Typography>
      );
    }

    // Submission - show loaded/unloaded status
    if (item.IsSub) {
      return (
        <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
          {item.IsLoadedSub ? (
            <>
              <CheckCircle sx={{ fontSize: 18, color: '#43B52E' }} />
              <Typography variant="body2" color="text.secondary">{t('grid.statusLoaded')}</Typography>
            </>
          ) : (
            <>
              <RadioButtonUnchecked sx={{ fontSize: 18, color: '#696969' }} />
              <Typography variant="body2" color="text.secondary">{t('grid.statusUnloaded')}</Typography>
            </>
          )}
        </Box>
      );
    }

    return null;
  };

  const handleRename = () => {
    if (contextMenu?.item) {
      setRenamingItem(contextMenu.item);
      setRenameValue(contextMenu.item.Name);
      setRenameDialogOpen(true);
    }
    handleCloseContextMenu();
  };

  // Rename item using oldName and newName
  const handleRenameConfirm = async () => {
    if (renamingItem && renameValue && renameValue !== renamingItem.Name) {
      await renameItem(renamingItem.Name, renameValue);
    }
    setRenameDialogOpen(false);
    setRenamingItem(null);
    setRenameValue('');
  };

  // Restore upload handler
  const { uploadFiles, uploadProgress, clearProgress } = useFileUpload({
    companyId: activeCompany?.Id?.toString(),
    currentPath,
    showSnackbars: false, // Progress panel shows all details
  });

  // Wrapper to match DragDropZone signature
  const handleUploadFiles = async (
    files: File[], 
    _targetPath: string, 
    validationFailures?: Array<{ fileName: string; path: string; error: string }>,
    validationWarnings?: Array<{ fileName: string; path: string; warning: string }>
  ) => {
    await uploadFiles(files, validationFailures, validationWarnings);
  };

  // Restore preview handler
  const handlePreview = () => {
    if (contextMenu?.item && onPreviewFile) {
      onPreviewFile(contextMenu.item);
    }
    handleCloseContextMenu();
  };

  // Fix onPreviewFile null argument
  // Usage: onPreviewFile(contextMenu.item) => onPreviewFile(contextMenu.item!)

  const handleDownload = () => {
    if (contextMenu?.item) {
      downloadItem(
        contextMenu.item.Id,
        contextMenu.item.Name,
        contextMenu.item.Type === 'Folder'
      );
    }
    handleCloseContextMenu();
  };

  const handleDelete = () => {
    if (contextMenu?.item) {
      setDeletingItems([contextMenu.item.Id]);
    }
    setDeleteDialogOpen(true);
    handleCloseContextMenu();
  };

  const handleDeleteConfirm = async () => {
    if (deletingItems.length > 0) {
      const items = deletingItems
        .map((id) => gridWithStatus.find((item) => item.Id === id))
        .filter(Boolean);
      const itemNames = items.map((item) => item!.Name);
      const isFolder = items.map((item) => item!.IsFolder ?? false);
      await deleteItems(deletingItems, itemNames, isFolder);
    }
    setDeleteDialogOpen(false);
    setDeletingItems([]);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <Typography>{t('grid.loadingFiles')}</Typography>
      </Box>
    );
  }

  // Show empty state for errors (path errors will auto-reset to root) or empty folders
  const showEmptyState = error || !gridData || gridData.length === 0;
  const showNoResults = !showEmptyState && filteredData.length === 0;

  if (showEmptyState || showNoResults) {
    // Determine the message to show
    let emptyMessage = t('grid.emptyFolder');
    if (showNoResults) {
      emptyMessage = t('grid.noSearchResults');
    }

    return (
      <DragDropZone
        targetPath={currentPath}
        onFilesDropped={handleUploadFiles}
        onItemsDropped={async (itemIds: string[], targetPath: string) => {
          // Get the names of items being moved
          const itemNames = itemIds
            .map((id) => gridData.find((item) => item.Id === id)?.Name)
            .filter(Boolean) as string[];
          await handleMoveItems(itemIds, itemNames, targetPath);
        }}
      >
        <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 220px)' }} data-testid="repo-grid-empty-container">
          <Table stickyHeader size="small" data-testid="repo-grid-empty-table">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ width: 50 }}>
                  <Checkbox disabled size="small" data-testid="repo-grid-select-all-checkbox-disabled" />
                </TableCell>
                <TableCell sx={{ width: 50 }}></TableCell>
                <TableCell>
                  <TableSortLabel disabled>{t('grid.columnName')}</TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel disabled>{t('grid.columnLastModified')}</TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel disabled>{t('grid.columnSize')}</TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: 50 }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow data-testid="repo-grid-empty-row">
                <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                  <Typography color="text.secondary" data-testid="repo-grid-empty-message">{emptyMessage}</Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Upload Progress Panel */}
        <UploadProgressPanel
          uploadProgress={uploadProgress}
          onClose={clearProgress}
        />
      </DragDropZone>
    );
  }

  return (
    <DragDropZone
      targetPath={currentPath}
      onFilesDropped={handleUploadFiles}
      onItemsDropped={async (itemIds: string[], targetPath: string) => {
        // Get the names of items being moved
        const itemNames = itemIds
          .map((id) => gridData.find((item) => item.Id === id)?.Name)
          .filter(Boolean) as string[];
        await handleMoveItems(itemIds, itemNames, targetPath);
      }}
    >
      <TableContainer component={Paper} data-testid="repo-grid-table-container">
        <Table size="small" data-testid="repo-grid-table">
          <TableHead data-testid="repo-grid-table-head">
            <TableRow>
              <TableCell padding="checkbox" sx={{ width: '40px' }}>
                <Checkbox
                  key={`select-all-${paginatedData.length}-${selectedIds.length}`}
                  data-testid="repo-grid-select-all-checkbox"
                  indeterminate={Boolean(
                    paginatedData.length > 0 && 
                    selectedIds.length > 0 && 
                    selectedIds.length < paginatedData.length
                  )}
                  checked={Boolean(
                    paginatedData.length > 0 && 
                    selectedIds.length > 0 && 
                    selectedIds.length === paginatedData.length
                  )}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell sx={{ width: '28px', textAlign: 'center' }}>{t('grid.columnMenu')}</TableCell>
              <TableCell sx={{ flex: 1 }}>
                <TableSortLabel
                  data-testid="repo-grid-sort-name"
                  active={orderBy === 'Name'}
                  direction={orderBy === 'Name' ? order : 'asc'}
                  onClick={() => handleSort('Name')}
                >
                  {t('grid.columnName')}
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: '140px' }}>
                <TableSortLabel
                  data-testid="repo-grid-sort-modified"
                  active={orderBy === 'LastModifiedString'}
                  direction={orderBy === 'LastModifiedString' ? order : 'asc'}
                  onClick={() => handleSort('LastModifiedString')}
                >
                  {t('grid.columnModified')}
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ width: '100px' }}>
                <TableSortLabel
                  data-testid="repo-grid-sort-size"
                  active={orderBy === 'Size'}
                  direction={orderBy === 'Size' ? order : 'asc'}
                  onClick={() => handleSort('Size')}
                >
                  {t('grid.columnSize')}
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: '120px' }}>{t('grid.columnStatus')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody data-testid="repo-grid-table-body">
            {paginatedData.map((item, index) => (
              <TableRow
                key={item.Id}
                hover
                selected={selectedIds.includes(item.Id)}
                onContextMenu={(e) => handleContextMenu(e, item)}
                onClick={(e) => handleRowClick(item, e)}
                onDoubleClick={(e) => handleRowDoubleClick(item, e)}
                draggable={enableDragDrop}
                onDragStart={(e) => {
                  if (enableDragDrop) {
                    const dragIds = selectedIds.includes(item.Id)
                      ? selectedIds
                      : [item.Id];
                    startDrag(dragIds, e);
                  }
                }}
                onDragEnd={() => {
                  if (enableDragDrop) {
                    endDrag();
                  }
                }}
                sx={{
                  cursor:
                    item.Type === 'Folder'
                      ? 'pointer'
                      : item.Type === 'File'
                        ? 'pointer'
                        : 'default',
                  opacity: enableDragDrop && selectedIds.includes(item.Id) ? 0.7 : 1,
                  '&:hover': enableDragDrop ? { backgroundColor: 'action.hover' } : {},
                }}
                data-testid={`repo-grid-row-${index}`}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    key={`checkbox-${item.Id}-${selectedIds.includes(item.Id || '')}`}
                    data-testid={`repo-grid-row-checkbox-${index}`}
                    checked={Boolean(item.Id && selectedIds.includes(item.Id))}
                    onChange={() => handleSelectOne(item.Id)}
                  />
                </TableCell>
                <TableCell align="center" sx={{ p: 0.5 }}>
                  <IconButton
                    data-testid={`repo-grid-row-menu-button-${index}`}
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleContextMenu(e, item);
                    }}
                  >
                    <MoreVert fontSize="small" />
                  </IconButton>
                </TableCell>
                <TableCell data-testid={`repo-grid-row-name-${index}`}>
                  <Box display="flex" alignItems="center" gap={1}>
                    {item.Type === 'Folder' ? (
                      <Folder sx={{ fontSize: 20 }} color="primary" data-testid={`repo-grid-row-folder-icon-${index}`} />
                    ) : (
                      <InsertDriveFile sx={{ fontSize: 20 }} color="action" data-testid={`repo-grid-row-file-icon-${index}`} />
                    )}
                    <Typography variant="body2" data-testid={`repo-grid-row-name-text-${index}`}>{item.Name}</Typography>
                  </Box>
                </TableCell>
                <TableCell data-testid={`repo-grid-row-modified-${index}`}>
                  <Typography variant="body2" color="text.secondary">
                    {item.LastModifiedString && formatDate(item.LastModifiedString)}
                  </Typography>
                </TableCell>
                <TableCell align="right" data-testid={`repo-grid-row-size-${index}`}>
                  {item.Type === 'File' && item.Size ? (
                    <Typography variant="body2" color="text.secondary">
                      {formatFileSize(item.Size)}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell align="center" data-testid={`repo-grid-row-status-${index}`}>
                  {renderStatusCell(item)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={sortedData.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 20, 50]}
        labelRowsPerPage={t('pagination.rowsPerPage')}
        labelDisplayedRows={({ from, to, count }) =>
          t('pagination.displayedRows', { from, to, count: count !== -1 ? count : `>${to}` })
        }
        data-testid="repo-grid-pagination"
      />

      {/* Context Menu */}
      <Menu
        data-testid="repo-grid-context-menu"
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        {contextMenu?.item?.Type === 'Folder' && (
          <MenuItem
            data-testid="repo-grid-context-menu-open"
            onClick={() => {
              if (contextMenu && contextMenu.item) {
                // Build new path from current path + folder name (same as Vue app)
                const newPath = currentPath
                  ? `${currentPath}/${contextMenu.item.Name}`
                  : contextMenu.item.Name;
                navigateToFolder(newPath);
                setSelectedItems([]);
                onClearSearch?.();
              }
              handleCloseContextMenu();
            }}
          >
            <ListItemIcon>
              <Folder fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('grid.contextOpen')}</ListItemText>
          </MenuItem>
        )}
        {/* TODO: Re-enable preview once file type handling is improved */}
        {contextMenu?.item?.Type === 'File' && onPreviewFile && (
          <MenuItem onClick={handlePreview} data-testid="repo-grid-context-menu-preview">
            <ListItemIcon>
              <Preview fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('grid.contextPreview')}</ListItemText>
          </MenuItem>
        )}
        {contextMenu?.item?.CanRename && (
          <MenuItem onClick={handleRename} data-testid="repo-grid-context-menu-rename">
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('grid.contextRename')}</ListItemText>
          </MenuItem>
        )}
        {contextMenu?.item?.CanDownload && (
          <MenuItem onClick={handleDownload} data-testid="repo-grid-context-menu-download">
            <ListItemIcon>
              <Download fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('grid.contextDownload')}</ListItemText>
          </MenuItem>
        )}
        {/* Duplicate preview menu item removed - using the one above */}
        {contextMenu?.item?.CanDelete && (
          <MenuItem onClick={handleDelete} data-testid="repo-grid-context-menu-delete">
            <ListItemIcon>
              <Delete fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('grid.contextDelete')}</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Rename Dialog */}
      <Dialog
        data-testid="repo-grid-rename-dialog"
        open={renameDialogOpen}
        onClose={() => setRenameDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle data-testid="repo-grid-rename-dialog-title">{t('grid.renameDialogTitle')}</DialogTitle>
        <DialogContent>
          <TextField
            data-testid="repo-grid-rename-dialog-input"
            autoFocus
            margin="dense"
            label={t('grid.renameNewNameLabel')}
            fullWidth
            variant="outlined"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialogOpen(false)} data-testid="repo-grid-rename-dialog-cancel-button">{t('grid.renameCancel')}</Button>
          <Button onClick={handleRenameConfirm} variant="contained" data-testid="repo-grid-rename-dialog-confirm-button">
            {t('grid.renameConfirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} data-testid="repo-grid-delete-dialog">
        <DialogTitle data-testid="repo-grid-delete-dialog-title">{t('grid.deleteDialogTitle')}</DialogTitle>
        <DialogContent>
          <Typography data-testid="repo-grid-delete-dialog-message">
            {t('grid.deleteConfirmMessage', { count: deletingItems.length })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} data-testid="repo-grid-delete-dialog-cancel-button">{t('grid.deleteCancel')}</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error" data-testid="repo-grid-delete-dialog-confirm-button">
            {t('grid.deleteConfirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Progress Panel */}
      <UploadProgressPanel
        uploadProgress={uploadProgress}
        onClose={clearProgress}
      />
    </DragDropZone>
  );
}
